/**
 * API Client for Internal Dashboard
 *
 * Centralized fetch wrapper with error handling.
 * All API calls go through here for consistency.
 */

const API_BASE = '/api/internal'
const VOICE_MODE_BASE = '/api/voice-mode'

const extractErrorMessage = (payload) => {
  if (!payload) return 'API request failed'

  if (typeof payload === 'string') {
    return payload
  }

  if (typeof payload.message === 'string' && payload.message.trim()) {
    return payload.message
  }

  if (typeof payload.error === 'string' && payload.error.trim()) {
    return payload.error
  }

  if (payload.error && typeof payload.error === 'object') {
    return extractErrorMessage(payload.error)
  }

  return 'API request failed'
}

/**
 * Core fetch implementation shared by all API bases.
 *
 * @param {string} base - Base URL prefix (e.g. '/api/internal')
 * @param {string} endpoint - Path after the base (e.g. '/accounts')
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<any>} Response data
 * @throws {Error} If response is not ok
 */
async function requestWithBase(base, endpoint, options = {}) {
  const url = `${base}${endpoint}`

  // Don't set Content-Type for FormData — the browser must set the multipart
  // boundary itself. JSON callers continue to get the default header.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData
  const defaultHeaders = isFormData ? {} : { 'Content-Type': 'application/json' }

  const response = await fetch(url, {
    credentials: 'include', // Include cookies for session auth
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  })

  // Handle non-JSON responses
  const contentType = response.headers.get('content-type')
  const isJson = contentType?.includes('application/json')

  if (!response.ok) {
    const error = isJson ? await response.json() : { message: response.statusText }
    const err = new Error(extractErrorMessage(error))
    err.status = response.status
    err.body = error
    throw err
  }

  return isJson ? response.json() : response.text()
}

/**
 * Make an API request to the internal API
 *
 * @param {string} endpoint - API endpoint (without /api/internal prefix)
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<any>} Response data
 * @throws {Error} If response is not ok
 */
export async function apiRequest(endpoint, options = {}) {
  return requestWithBase(API_BASE, endpoint, options)
}

/**
 * Make an internal API request that returns a binary payload.
 *
 * @param {string} endpoint - API endpoint (without /api/internal prefix)
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<Response>} Successful fetch response for callers to read as blob/arrayBuffer
 */
export async function apiBlobRequest(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`

  const response = await fetch(url, {
    credentials: 'include',
    ...options,
    headers: {
      ...options.headers,
    },
  })

  if (!response.ok) {
    const contentType = response.headers.get('content-type')
    const isJson = contentType?.includes('application/json')
    const error = isJson ? await response.json() : { message: await response.text() }
    const err = new Error(extractErrorMessage(error))
    err.status = response.status
    err.body = error
    throw err
  }

  return response
}

/**
 * Internal API helpers (GET/POST/PUT/PATCH/DELETE to /api/internal)
 */
export const api = {
  get: (endpoint) => apiRequest(endpoint, { method: 'GET' }),
  post: (endpoint, data) =>
    apiRequest(endpoint, { method: 'POST', body: JSON.stringify(data) }),
  put: (endpoint, data) =>
    apiRequest(endpoint, { method: 'PUT', body: JSON.stringify(data) }),
  patch: (endpoint, data) =>
    apiRequest(endpoint, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (endpoint) => apiRequest(endpoint, { method: 'DELETE' }),
}

/**
 * Voice-mode API helpers (GET/POST to /api/voice-mode)
 */
export const voiceModeApi = {
  get: (endpoint) => requestWithBase(VOICE_MODE_BASE, endpoint, { method: 'GET' }),
  post: (endpoint, data) =>
    requestWithBase(VOICE_MODE_BASE, endpoint, { method: 'POST', body: JSON.stringify(data) }),
}
